import { isBackupConfirmed } from '../security/backupVerify';
import { useState, useEffect } from 'react';
import { useWallet } from '../context/WalletContext';
import styles from './Welcome.module.css';
import { ClaimNameStep } from '../components/ClaimNameStep';
import { setPendingName } from '../storage/pendingName';
import { RecoveryPhraseGrid } from '../components/RecoveryPhraseGrid';
import {
  isPhraseConfirmed,
  normalizeMnemonicWords,
  parseRecoveryPhrase,
  pickConfirmationPositions,
} from '../crypto/recoveryPhrase';

export type WelcomeProps = {
  /** From dashboard while unlocked: open multi-account create/import flow */
  addAccountWhileUnlocked?: boolean;
  /** Close add-account flow and return to dashboard */
  onLeaveAddAccount?: () => void;
};

const ASSETS = '/assets';

const MASCOT_BY_STEP: Record<string, string> = {
  choose: 'mascot-excited.webp',
  unlock: 'mascot-thinking.webp',
  create: 'mascot-default.webp',
  import: 'mascot-default.webp',
  backup: 'mascot-default.webp',
};

type Step = 'choose' | 'create' | 'import' | 'unlock' | 'backup' | 'claim-name' | 'add-pick';

/** When adding a 2+ account to an existing vault (web parity with extension). */
type AccountFlow = 'initial' | 'add-secondary';

export function Welcome({ addAccountWhileUnlocked = false, onLeaveAddAccount }: WelcomeProps = {}) {
  const {
    hasWallet,
    storedAddressHint,
    unlock,
    createWallet,
    importWallet,
    createAnotherAccount,
    importAnotherAccount,
    createWalletWithPhrase,
    importWalletFromPhrase,
    network,
  } = useWallet();

  const [step, setStep] = useState<Step>(() => (addAccountWhileUnlocked ? 'add-pick' : hasWallet ? 'unlock' : 'choose'));
  const [accountFlow, setAccountFlow] = useState<AccountFlow>(() => (addAccountWhileUnlocked ? 'add-secondary' : 'initial'));

  useEffect(() => {
    if (addAccountWhileUnlocked && !hasWallet) {
      onLeaveAddAccount?.();
    }
  }, [addAccountWhileUnlocked, hasWallet, onLeaveAddAccount]);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [privateKeyHex, setPrivateKeyHex] = useState('');
  const [backupKeyHex, setBackupKeyHex] = useState('');
  const [backupAcknowledged, setBackupAcknowledged] = useState(false);
  const [backupConfirmText, setBackupConfirmText] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copiedBackup, setCopiedBackup] = useState(false);
  /** New accounts default to a recovery phrase; "key" keeps the classic private-key backup. */
  const [createMethod, setCreateMethod] = useState<'phrase' | 'key'>('phrase');
  const [importMethod, setImportMethod] = useState<'phrase' | 'key'>('phrase');
  const [phraseInput, setPhraseInput] = useState('');
  /** Memory only: never written to storage, logs or the URL; cleared as soon as backup is finished. */
  const [backupPhrase, setBackupPhrase] = useState('');
  const [confirmPositions, setConfirmPositions] = useState<number[]>([]);
  const [confirmWords, setConfirmWords] = useState<string[]>(['', '', '']);
  const phraseBackupWords = backupPhrase ? normalizeMnemonicWords(backupPhrase) : [];
  const phraseConfirmed = backupPhrase ? isPhraseConfirmed(backupPhrase, confirmPositions, confirmWords) : false;
  async function handleUnlock(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await unlock(password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid password');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      if (createMethod === 'phrase') {
        const { phrase } = await createWalletWithPhrase(password, accountFlow === 'add-secondary');
        setBackupPhrase(phrase);
        setConfirmPositions(pickConfirmationPositions(normalizeMnemonicWords(phrase).length, 3));
        setConfirmWords(['', '', '']);
        setBackupKeyHex('');
        setStep('backup');
        setBackupAcknowledged(false);
        return;
      }
      const { privateKeyHex: keyHex } =
        accountFlow === 'add-secondary' ? await createAnotherAccount(password) : await createWallet(password);
      setBackupPhrase('');
      setBackupKeyHex(keyHex);
      setStep('backup');
      setBackupAcknowledged(false);
    setBackupConfirmText('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create wallet');
    } finally {
      setLoading(false);
    }
  }

  async function handleBackupContinue() {
    if (step === 'backup' && accountFlow === 'initial' && !addAccountWhileUnlocked) {
      setError('');
      setStep('claim-name');
      return;
    }
    await finishCreate();
  }

  async function finishCreate() {
    setError('');
    setLoading(true);
    try {
      await unlock(password);
      setBackupPhrase('');
      setConfirmWords(['', '', '']);
      setAccountFlow('initial');
      if (addAccountWhileUnlocked) {
        onLeaveAddAccount?.();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unlock');
    } finally {
      setLoading(false);
    }
  }

  async function copyBackupKey() {
    if (!backupKeyHex) return;
    await navigator.clipboard.writeText(backupKeyHex);
    setCopiedBackup(true);
    setTimeout(() => setCopiedBackup(false), 2000);
  }

  async function handleImport(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (importMethod === 'phrase') {
      let canonical: string;
      try {
        canonical = parseRecoveryPhrase(phraseInput);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'That recovery phrase is not valid.');
        return;
      }
      if (password.length < 8) {
        setError('Password must be at least 8 characters');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      setLoading(true);
      try {
        await importWalletFromPhrase(password, canonical, accountFlow === 'add-secondary');
        setPhraseInput('');
        if (accountFlow === 'add-secondary') {
          setAccountFlow('initial');
          if (addAccountWhileUnlocked) {
            onLeaveAddAccount?.();
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to import wallet');
      } finally {
        setLoading(false);
      }
      return;
    }
    const hex = privateKeyHex.replace(/\s/g, '').replace(/^0x/i, '');
    if (hex.length !== 64 || !/^[0-9a-fA-F]+$/.test(hex)) {
      setError('Private key must be 64 hex characters (32 bytes)');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      if (accountFlow === 'add-secondary') {
        await importAnotherAccount(password, hex);
        setAccountFlow('initial');
        if (addAccountWhileUnlocked) {
          onLeaveAddAccount?.();
        }
      } else {
        await importWallet(password, hex);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import wallet');
    } finally {
      setLoading(false);
    }
  }

  if (step === 'choose') {
    return (
      <div className={`${styles.wrap} ${styles.heroWrap} page-app`}>
        <div className={`${styles.card} ${styles.heroCard}`}>
          <img src={`${ASSETS}/${MASCOT_BY_STEP.choose}`} alt="" className={styles.mascot} aria-hidden />
          <h1 className={styles.title}>Welcome to Boing Express</h1>
          <p className={styles.subtitle}>Your gateway to Boing</p>
          <div className={styles.actions}>
            <button type="button" className={`${styles.primary} ${styles.heroPrimary}`} data-testid="wallet-landing-create" onClick={() => setStep('create')}>
              Create New Wallet
            </button>
            <button type="button" className={`${styles.secondary} ${styles.heroSecondary}`} data-testid="wallet-landing-import" onClick={() => setStep('import')}>
              Import Existing Wallet
            </button>
          </div>
          <p className={styles.keysNote} data-testid="wallet-keys-note">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            Your keys never leave your device
          </p>
          <p className={styles.testnetNote} data-testid="wallet-testnet-note">Public testnet — mainnet not live</p>
        </div>
      </div>
    );
  }

  if (step === 'unlock') {
    return (
      <div className={`${styles.wrap} ${styles.heroWrap} page-app`}>
        <div className={`${styles.card} ${styles.heroCard}`}>
          <img src={`${ASSETS}/${MASCOT_BY_STEP.unlock}`} alt="" className={styles.mascot} aria-hidden />
          <h1 className={styles.title}>Unlock wallet</h1>
          {storedAddressHint && (
            <p className={styles.hint}>Address: {storedAddressHint}</p>
          )}
          <form onSubmit={handleUnlock} className={styles.form}>
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.input}
              autoComplete="current-password"
              data-testid="wallet-unlock-password"
            />
            {error && <p className={styles.error}>{error}</p>}
            <button type="submit" className={styles.primary} disabled={loading} data-testid="wallet-unlock-submit">
              {loading ? 'Unlocking…' : 'Unlock'}
            </button>
          </form>
          {hasWallet && (
            <button
              type="button"
              className={`${styles.secondary} ${styles.secondaryFullWidth}`}
              data-testid="wallet-unlock-add-account"
              onClick={() => {
                setError('');
                setAccountFlow('add-secondary');
                setStep('add-pick');
              }}
            >
              Add another account
            </button>
          )}
          {!hasWallet && (
            <button type="button" className={styles.textBtn} onClick={() => setStep('choose')}>
              Back
            </button>
          )}
        </div>
      </div>
    );
  }

  if (step === 'add-pick') {
    return (
      <div className={`${styles.wrap} ${styles.heroWrap} page-app`}>
        <div className={`${styles.card} ${styles.heroCard}`}>
          <img src={`${ASSETS}/mascot-default.webp`} alt="" className={styles.mascot} aria-hidden />
          <h1 className={styles.title}>Add another account</h1>
          <p className={styles.subtitle}>
            New keys are encrypted with your password and stored alongside your existing wallet. Use Boing Express on
            desktop for the same multi-account vault.
          </p>
          <div className={styles.actions}>
            <button type="button" className={styles.primary} data-testid="wallet-add-pick-create" onClick={() => setStep('create')}>
              Create new keypair
            </button>
            <button type="button" className={styles.secondary} data-testid="wallet-add-pick-import" onClick={() => setStep('import')}>
              Import private key
            </button>
          </div>
          <button
            type="button"
            className={styles.textBtn}
            data-testid="wallet-add-pick-back"
            onClick={() => {
              if (addAccountWhileUnlocked && onLeaveAddAccount) {
                onLeaveAddAccount();
                return;
              }
              setAccountFlow('initial');
              setStep('unlock');
            }}
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  if (step === 'backup') {
    return (
      <div className={`${styles.wrap} ${styles.heroWrap} page-app`}>
        <div className={`${styles.card} ${styles.heroCard}`}>
          <img src={`${ASSETS}/${MASCOT_BY_STEP.backup}`} alt="" className={styles.mascot} aria-hidden />
          <h1 className={styles.title}>{backupPhrase ? 'Write down your recovery phrase' : 'Back up your private key'}</h1>
          <p className={styles.subtitle}>
            {backupPhrase
              ? 'These words are the only way to get your account back if you lose this device. Write them on paper, in order, and keep them offline. Anyone who has them controls your wallet. You won’t see them again here.'
              : 'Save this key in a safe place. Anyone with it can control your wallet. You won’t see it again here.'}
          </p>
          {backupPhrase ? (
            <>
              <RecoveryPhraseGrid words={phraseBackupWords} />
              <label className={styles.checkboxWrap}>
                <input
                  type="checkbox"
                  checked={backupAcknowledged}
                  onChange={(e) => setBackupAcknowledged(e.target.checked)}
                  className={styles.checkbox}
                  data-testid="wallet-backup-ack"
                />
                <span>I have written down my recovery phrase and stored it safely</span>
              </label>
              <p className={styles.hint}>To check you wrote it down correctly, type these words from your phrase:</p>
              {confirmPositions.map((pos, i) => (
                <input
                  key={pos}
                  type="text"
                  className={styles.input}
                  autoComplete="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  placeholder={`Word #${pos + 1}`}
                  aria-label={`Word number ${pos + 1}`}
                  value={confirmWords[i] ?? ''}
                  onChange={(e) => setConfirmWords((w) => w.map((x, j) => (j === i ? e.target.value : x)))}
                  data-testid={`wallet-phrase-confirm-${i}`}
                />
              ))}
            </>
          ) : (
          <>
          <div className={styles.backupKeyWrap}>
            <code className={styles.backupKey} data-testid="wallet-backup-key">{backupKeyHex}</code>
            <button type="button" className={styles.copyBtn} onClick={copyBackupKey}>
              {copiedBackup ? 'Copied' : 'Copy'}
            </button>
          </div>
          <label className={styles.checkboxWrap}>
            <input
              type="checkbox"
              checked={backupAcknowledged}
              onChange={(e) => setBackupAcknowledged(e.target.checked)}
              className={styles.checkbox}
              data-testid="wallet-backup-ack"
            />
            <span>I have saved my private key in a safe place</span>
          </label>
          <label className={styles.checkboxWrap} htmlFor="backup-confirm">
            <span>Type the last 6 characters of your key to confirm you saved it</span>
          </label>
          <input
            id="backup-confirm"
            type="text"
            className={styles.input}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            maxLength={12}
            placeholder="Last 6 characters"
            value={backupConfirmText}
            onChange={(e) => setBackupConfirmText(e.target.value)}
            data-testid="wallet-backup-confirm"
          />
          </>
          )}
          {error && <p className={styles.error}>{error}</p>}
          <button
            type="button"
            className={styles.primary}
            data-testid="wallet-backup-continue"
            onClick={handleBackupContinue}
            disabled={
              loading ||
              !backupAcknowledged ||
              (backupPhrase ? !phraseConfirmed : !isBackupConfirmed(backupKeyHex, backupConfirmText))
            }
          >
            {loading ? 'Unlocking…' : "I've backed it up — Continue"}
          </button>
        </div>
      </div>
    );
  }

  if (step === 'claim-name') {
    return (
      <div className={`${styles.wrap} ${styles.heroWrap} page-app`}>
        <ClaimNameStep
          rpcUrl={network.config.rpcUrl}
          mascotSrc={`${ASSETS}/${MASCOT_BY_STEP.create}`}
          busy={loading}
          error={error}
          onReserve={(label) => {
            setPendingName({ name: label, createdAt: Date.now() });
            void finishCreate();
          }}
          onSkip={() => void finishCreate()}
        />
      </div>
    );
  }

  if (step === 'create') {
    return (
      <div className={`${styles.wrap} ${styles.heroWrap} page-app`}>
        <div className={`${styles.card} ${styles.heroCard}`}>
          <img src={`${ASSETS}/${MASCOT_BY_STEP.create}`} alt="" className={styles.mascot} aria-hidden />
          <h1 className={styles.title}>{accountFlow === 'add-secondary' ? 'Create another account' : 'Create wallet'}</h1>
          <p className={styles.subtitle}>
            {createMethod === 'phrase'
              ? 'We’ll give you 24 words to write down. They are the only way to get your account back if you lose this device — we don’t store them on any server.'
              : 'Generate a new Ed25519 keypair. Back up your key — we don’t store it on any server.'}
          </p>
          <form onSubmit={handleCreate} className={styles.form}>
            <fieldset style={{ border: 0, padding: 0, margin: 0, textAlign: 'left' }}>
              <legend className={styles.hint}>How do you want to back up this account?</legend>
              <label className={styles.checkboxWrap}>
                <input
                  type="radio"
                  name="create-method"
                  checked={createMethod === 'phrase'}
                  onChange={() => setCreateMethod('phrase')}
                  className={styles.checkbox}
                  data-testid="wallet-create-method-phrase"
                />
                <span>Recovery phrase (24 words) — recommended</span>
              </label>
              <label className={styles.checkboxWrap}>
                <input
                  type="radio"
                  name="create-method"
                  checked={createMethod === 'key'}
                  onChange={() => setCreateMethod('key')}
                  className={styles.checkbox}
                  data-testid="wallet-create-method-key"
                />
                <span>Private key only (64-character hex)</span>
              </label>
            </fieldset>
            <input
              type="password"
              placeholder="Password (min 8 characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.input}
              autoComplete="new-password"
              data-testid="wallet-create-password"
            />
            <input
              type="password"
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={styles.input}
              autoComplete="new-password"
              data-testid="wallet-create-password-confirm"
            />
            {error && <p className={styles.error}>{error}</p>}
            <button type="submit" className={styles.primary} disabled={loading} data-testid="wallet-create-submit">
              {loading ? 'Creating…' : accountFlow === 'add-secondary' ? 'Create account' : 'Create wallet'}
            </button>
          </form>
          <button
            type="button"
            className={styles.textBtn}
            onClick={() => setStep(accountFlow === 'add-secondary' ? 'add-pick' : 'choose')}
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  // import
  return (
    <div className={`${styles.wrap} ${styles.heroWrap} page-app`}>
      <div className={`${styles.card} ${styles.heroCard}`}>
        <img src={`${ASSETS}/${MASCOT_BY_STEP.import}`} alt="" className={styles.mascot} aria-hidden />
        <h1 className={styles.title}>{accountFlow === 'add-secondary' ? 'Import another account' : 'Import wallet'}</h1>
        <p className={styles.subtitle}>
          {importMethod === 'phrase'
            ? 'Enter your 12- or 24-word recovery phrase. It is turned into your account on this device and encrypted with your password.'
            : 'Enter your 64-character hex private key (32 bytes). It will be encrypted with your password.'}
        </p>
        <form onSubmit={handleImport} className={styles.form}>
          <fieldset style={{ border: 0, padding: 0, margin: 0, textAlign: 'left' }}>
            <label className={styles.checkboxWrap}>
              <input
                type="radio"
                name="import-method"
                checked={importMethod === 'phrase'}
                onChange={() => setImportMethod('phrase')}
                className={styles.checkbox}
                data-testid="wallet-import-method-phrase"
              />
              <span>Recovery phrase</span>
            </label>
            <label className={styles.checkboxWrap}>
              <input
                type="radio"
                name="import-method"
                checked={importMethod === 'key'}
                onChange={() => setImportMethod('key')}
                className={styles.checkbox}
                data-testid="wallet-import-method-key"
              />
              <span>Private key</span>
            </label>
          </fieldset>
          {importMethod === 'phrase' ? (
            <textarea
              placeholder="word1 word2 word3 …"
              value={phraseInput}
              onChange={(e) => setPhraseInput(e.target.value)}
              className={styles.textarea}
              rows={3}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              data-testid="wallet-import-phrase"
            />
          ) : (
          <textarea
            placeholder="0x... or 64 hex chars"
            value={privateKeyHex}
            onChange={(e) => setPrivateKeyHex(e.target.value)}
            className={styles.textarea}
            rows={3}
            data-testid="wallet-import-key"
          />
          )}
          <input
            type="password"
            placeholder="Password (min 8 characters)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={styles.input}
            autoComplete="new-password"
            data-testid="wallet-import-password"
          />
          <input
            type="password"
            placeholder="Confirm password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className={styles.input}
            autoComplete="new-password"
            data-testid="wallet-import-password-confirm"
          />
          {error && <p className={styles.error}>{error}</p>}
          <button type="submit" className={styles.primary} disabled={loading} data-testid="wallet-import-submit">
            {loading ? 'Importing…' : accountFlow === 'add-secondary' ? 'Import account' : 'Import wallet'}
          </button>
        </form>
        <button
          type="button"
          className={styles.textBtn}
          onClick={() => setStep(accountFlow === 'add-secondary' ? 'add-pick' : 'choose')}
        >
          Back
        </button>
      </div>
    </div>
  );
}
